import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Table, 
  Card, 
  Badge, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Pagination,
  Button,
  Modal
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { edgeDeviceAPI, clearCache } from '../utils/api';
import type { EdgeDevice, CreateEdgeDeviceRequest } from '../types/edgedevice';
import { formatDateTimeInNurseryTimezone } from '../utils/timezone';

// Lightweight icon replacements to reduce bundle size
const EdgeDeviceIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1">
    <path d="M6 1a1 1 0 0 0-1 1v1H3a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2V2a1 1 0 0 0-1-1H6zM5 2h6v1H5V2zm7 2a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h9z"/>
    <path d="M4 6h8v1H4V6zm0 2h8v1H4V8zm0 2h8v1H4v-1z"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
  </svg>
);

const ClearIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1">
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
  </svg>
);

// Memoized EdgeDevice Card Component for Mobile
const EdgeDeviceCard = memo(({ edgeDevice }: {
  edgeDevice: EdgeDevice;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'edgeDevices' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInNurseryTimezone(dateString, currentNursery?.timezone || 'UTC');
  }, [currentNursery?.timezone]);

  const getStatusBadge = useCallback((status: number) => {
    return status === 1 ? (
      <Badge bg="success">{tc('status.active')}</Badge>
    ) : (
      <Badge bg="danger">{tc('status.inactive')}</Badge>
    );
  }, [tc]);

  return (
    <Card className="mobile-edgedevice-card m-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <EdgeDeviceIcon />
            <div>
              <h6 className="mb-1 fw-bold">{renderCellValue(edgeDevice.name)}</h6>
              <small className="text-muted">ID: {renderCellValue(edgeDevice.id)}</small>
            </div>
          </div>
          {getStatusBadge(edgeDevice.status)}
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('nurseryIdColumn')}</small>
          <span className="fw-medium">{renderCellValue(edgeDevice.nursery_id)}</span>
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('apiTokenHash')}</small>
          <code className="small">{renderCellValue(edgeDevice.api_token_hash ? edgeDevice.api_token_hash.substring(0, 12) + '...' : tc('messages.na'))}</code>
        </div>

        <div className="mb-2">
          <small className="text-muted d-block">{t('registeredAt')}</small>
          <small>{formatDateTime(edgeDevice.registered_at)}</small>
        </div>

        <div className="mb-2">
          <small className="text-muted d-block">{t('tokenExpiration')}</small>
          <small>{formatDateTime(edgeDevice.token_expiration_date)}</small>
        </div>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {tc('labels.created')}: {edgeDevice.created_at ? formatDateTime(edgeDevice.created_at) : tc('messages.na')}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
});

EdgeDeviceCard.displayName = 'EdgeDeviceCard';

// Memoized Table Row Component
const EdgeDeviceTableRow = memo(({ 
  edgeDevice, 
  nurseryTimezone
}: { 
  edgeDevice: EdgeDevice; 
  nurseryTimezone: string;
}) => {
  const { t: tc } = useTranslation('common');
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return tc('messages.na');
    return formatDateTimeInNurseryTimezone(dateString, nurseryTimezone);
  }, [nurseryTimezone, tc]);

  const getStatusBadge = useCallback((status: number) => {
    return status === 1 ? (
      <Badge bg="success">{tc('status.active')}</Badge>
    ) : (
      <Badge bg="danger">{tc('status.inactive')}</Badge>
    );
  }, [tc]);

  return (
    <tr>
      <td>{getStatusBadge(edgeDevice.status)}</td>
      <td>
        <div className="d-flex align-items-center">
          <EdgeDeviceIcon />
          <span className="fw-medium">{renderCellValue(edgeDevice.name)}</span>
        </div>
      </td>
      <td><code className="small">{renderCellValue(edgeDevice.id)}</code></td>
      <td><code className="small">{renderCellValue(edgeDevice.nursery_id)}</code></td>
      <td><code className="small">{renderCellValue(edgeDevice.api_token_hash ? edgeDevice.api_token_hash.substring(0, 12) + '...' : tc('messages.na'))}</code></td>
      <td>
        <small className="text-muted">
          {formatDateTime(edgeDevice.registered_at)}
        </small>
      </td>
      <td>
        <small className="text-muted">
          {formatDateTime(edgeDevice.token_expiration_date)}
        </small>
      </td>
      <td>
        <small className="text-muted">
          {edgeDevice.created_at ? formatDateTime(edgeDevice.created_at) : tc('messages.na')}
        </small>
      </td>
    </tr>
  );
});

EdgeDeviceTableRow.displayName = 'EdgeDeviceTableRow';

const EdgeDevices: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'edgeDevices' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  
  // Get nursery timezone with fallback
  const nurseryTimezone = currentNursery?.timezone || 'UTC';
  
  const [edgeDevices, setEdgeDevices] = useState<EdgeDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<CreateEdgeDeviceRequest>(() => {
    const storedNursery = localStorage.getItem('currentNursery');
    let initialTz = 'UTC';
    let nurseryId = '';
    if (storedNursery) {
      try {
        const parsed = JSON.parse(storedNursery);
        initialTz = parsed.timezone || 'UTC';
        nurseryId = parsed.nursery_id || '';
      } catch (e) {}
    }
    
    const getInitialDateTime = (tz: string, daysOffset = 0) => {
      const now = new Date();
      if (daysOffset !== 0) {
        now.setDate(now.getDate() + daysOffset);
      }
      const nurseryDate = new Date(now.toLocaleString('en-US', { timeZone: tz }));
      const Y = nurseryDate.getFullYear();
      const M = String(nurseryDate.getMonth() + 1).padStart(2, '0');
      const D = String(nurseryDate.getDate()).padStart(2, '0');
      const h = String(nurseryDate.getHours()).padStart(2, '0');
      const m = String(nurseryDate.getMinutes()).padStart(2, '0');
      return `${Y}-${M}-${D}T${h}:${m}`;
    };
    
    return {
      name: '',
      nursery_id: nurseryId,
      api_token_hash: '',
      registered_at: getInitialDateTime(initialTz),
      status: 1,
      token_expiration_date: getInitialDateTime(initialTz, 7)
    };
  });

  // Check if user has edge device permissions
  const { canManageEdgeDevices, canAdd } = useRolePermissions();

  const fetchEdgeDevices = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await edgeDeviceAPI.getEdgeDevices(currentNursery.nursery_id);
      
      // Handle both array response and object response formats
      if (Array.isArray(response)) {
        setEdgeDevices(response);
      } else {
        setEdgeDevices(response.devices || []);
      }
    } catch (error: any) {
      console.error('Error fetching edge devices:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery, t]);

  useEffect(() => {
    fetchEdgeDevices();
  }, [fetchEdgeDevices]);

  // Update nursery_id in form when currentNursery changes
  useEffect(() => {
    if (currentNursery) {
      setFormData(prev => ({ ...prev, nursery_id: currentNursery.nursery_id }));
    }
  }, [currentNursery]);

  // Memoized filtered edge devices to prevent unnecessary re-computations
  const filteredEdgeDevices = useMemo(() => {
    if (!searchTerm.trim()) return edgeDevices;
    
    const searchLower = searchTerm.toLowerCase();
    return edgeDevices.filter(device => 
      device.name?.toLowerCase().includes(searchLower) ||
      device.id?.toString().includes(searchLower) ||
      device.nursery_id?.toLowerCase().includes(searchLower) ||
      device.api_token_hash?.toLowerCase().includes(searchLower)
    );
  }, [edgeDevices, searchTerm]);

  const totalPages = Math.ceil(filteredEdgeDevices.length / rowsPerPage);
  const paginatedEdgeDevices = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredEdgeDevices.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredEdgeDevices, page, rowsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  }, []);



  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Modal handlers
  const handleShowModal = useCallback(() => {
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    
    const tz = currentNursery?.timezone || 'UTC';
    const getInitialDateTime = (t: string | Date, daysOffset = 0) => {
      const d = new Date(t);
      if (daysOffset !== 0) {
        d.setDate(d.getDate() + daysOffset);
      }
      const nurseryDate = new Date(d.toLocaleString('en-US', { timeZone: tz }));
      const Y = nurseryDate.getFullYear();
      const M = String(nurseryDate.getMonth() + 1).padStart(2, '0');
      const D = String(nurseryDate.getDate()).padStart(2, '0');
      const h = String(nurseryDate.getHours()).padStart(2, '0');
      const m = String(nurseryDate.getMinutes()).padStart(2, '0');
      return `${Y}-${M}-${D}T${h}:${m}`;
    };

    setFormData({
      name: '',
      nursery_id: currentNursery?.nursery_id || '',
      api_token_hash: '',
      registered_at: getInitialDateTime(new Date()),
      status: 1,
      token_expiration_date: getInitialDateTime(new Date(), 7)
    });
  }, [currentNursery]);

  const handleFormChange = useCallback((field: keyof CreateEdgeDeviceRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentNursery) return;

    try {
      setModalLoading(true);
      setModalError(null);
      setModalSuccess(null);

      await edgeDeviceAPI.createEdgeDevice(formData);
      
      setModalSuccess(t('createSuccess'));
      
      // Refresh the list
      await fetchEdgeDevices();
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error creating edge device:', error);
      setModalError(error.response?.data?.message || error.message || t('createFailed'));
    } finally {
      setModalLoading(false);
    }
  }, [formData, currentNursery, fetchEdgeDevices, handleCloseModal]);

  // Generate pagination items
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Force cache clear when nursery changes
  useEffect(() => {
    if (currentNursery) {
      clearCache(`edgedevices_${currentNursery.nursery_id}`);
      setSearchTerm('');
      setPage(1);
    }
  }, [currentNursery]);

  // If user doesn't have edge device permissions, show access denied
  if (!canManageEdgeDevices) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="px-3">
          <Alert variant="danger" className="mt-4">
            <Alert.Heading>{t('accessDeniedTitle')}</Alert.Heading>
            <p>
              {t('accessDeniedBody')}
            </p>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <h2 className="fw-bold text-primary mb-3 mb-md-0">
            {t('title')}
          </h2>
          {currentNursery && canAdd('edge_devices') && (
            <Button variant="primary" onClick={handleShowModal}>
              <PlusIcon />
              {t('addDevice')}
            </Button>
          )}
        </div>
        
        {currentNursery && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentNursery.description} (ID: {currentNursery.nursery_id})
          </Alert>
        )}

        {!currentNursery && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectNursery', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Search Bar */}
        {currentNursery && (
          <Card className="mb-4">
            <Card.Body>
              <InputGroup>
                <InputGroup.Text>
                  <SearchIcon />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={clearSearch}>
                    <ClearIcon />
                  </Button>
                )}
              </InputGroup>
              {searchTerm && (
                <small className="text-muted mt-2 d-block">
                  {t('found', { count: filteredEdgeDevices.length, term: searchTerm })}
                </small>
              )}
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loading')}</span>
          </div>
        )}

        {currentNursery && !loading && (
          <Card>
            <Card.Body className="p-0">
              {/* Mobile Card View */}
              <div className="d-md-none">
                {paginatedEdgeDevices.length > 0 ? (
                  paginatedEdgeDevices.map((edgeDevice) => (
                    <EdgeDeviceCard
                      key={edgeDevice.id}
                      edgeDevice={edgeDevice}
                    />
                  ))
                ) : (
                  <div className="text-center py-5">
                    <EdgeDeviceIcon />
                    <h5>{t('noTitle')}</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? t('noMatch', { term: searchTerm, nursery: currentNursery.description })
                        : t('noneRegistered', { nursery: currentNursery.description })
                      }
                    </p>
                    <Button variant="primary" onClick={handleShowModal}>
                      <PlusIcon />
                      {t('addFirstDevice')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{tc('labels.status')}</th>
                        <th>{t('deviceName')}</th>
                        <th>ID</th>
                        <th>{t('nurseryIdColumn')}</th>
                        <th>{t('apiTokenHash')}</th>
                        <th>{t('registeredAt')}</th>
                        <th>{t('tokenExpiration')}</th>
                        <th>{tc('labels.created')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEdgeDevices.length > 0 ? (
                        paginatedEdgeDevices.map((edgeDevice) => (
                          <EdgeDeviceTableRow
                            key={edgeDevice.id}
                            edgeDevice={edgeDevice}
                            nurseryTimezone={nurseryTimezone}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center py-5">
                            <EdgeDeviceIcon />
                            <h5>{t('noTitle')}</h5>
                            <p className="text-muted mb-3">
                              {searchTerm 
                                ? t('noMatch', { term: searchTerm, nursery: currentNursery.description })
                                : t('noneRegistered', { nursery: currentNursery.description })
                              }
                            </p>
                            <Button variant="primary" onClick={handleShowModal}>
                              <PlusIcon />
                              {t('addFirstDevice')}
                            </Button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {filteredEdgeDevices.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <span className="text-muted small">
                    {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, filteredEdgeDevices.length), total: filteredEdgeDevices.length, entity: t('title').toLowerCase() })}
                  </span>
                  <Pagination className="mb-0">
                    <Pagination.Prev 
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next 
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {!currentNursery && !loading && (
          <Card>
            <Card.Body className="text-center py-5">
              <h5>{tc('messages.selectNurseryTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectNurseryBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Create Edge Device Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {modalSuccess && (
                <Alert variant="success" className="mb-3">
                  {modalSuccess}
                </Alert>
              )}
              
              {modalError && (
                <Alert variant="danger" className="mb-3">
                  {modalError}
                </Alert>
              )}

              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>{t('nameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>{t('nurseryIdLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.nursery_id}
                      onChange={(e) => handleFormChange('nursery_id', e.target.value)}
                      placeholder={t('nurseryIdPlaceholder')}
                      required
                      readOnly
                    />
                  </Form.Group>
                </div>

                <div className="col-md-12 mb-3">
                  <Form.Group>
                    <Form.Label>{t('apiTokenLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.api_token_hash}
                      onChange={(e) => handleFormChange('api_token_hash', e.target.value)}
                      placeholder={t('apiTokenPlaceholder')}
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>{t('registeredAtLabel')}</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={formData.registered_at}
                      onChange={(e) => handleFormChange('registered_at', e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>{t('statusLabel')}</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(e) => handleFormChange('status', parseInt(e.target.value))}
                      required
                    >
                      <option value={1}>{tc('status.active')}</option>
                      <option value={0}>{tc('status.inactive')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-md-12 mb-3">
                  <Form.Group>
                    <Form.Label>{t('tokenExpirationLabel')}</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={formData.token_expiration_date}
                      onChange={(e) => handleFormChange('token_expiration_date', e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('loading.creating')}
                  </>
                ) : (
                  <>
                    <PlusIcon />
                    {t('createDevice')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default EdgeDevices; 